<div class="dynamic-search-dialog">
  <div class="dialog-header">
    <h2 mat-dialog-title>
      <mat-icon>search</mat-icon>
      Advanced Search
    </h2>
    <button mat-icon-button (click)="onClose()" class="close-btn">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <mat-dialog-content>
    <form [formGroup]="searchForm" (ngSubmit)="onSearch()" class="search-form">
      <div class="search-bar">
        <mat-form-field appearance="outline" class="search-type-field">
          <mat-select formControlName="searchType">
            <mat-option *ngFor="let type of searchTypes" [value]="type.value">
              {{ type.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="search-input-field">
          <input matInput #searchInput id="search-input" formControlName="searchQuery" placeholder="Search for property, unit #..." (keyup.enter)="onSearch()">
          <mat-icon matPrefix>search</mat-icon>
        </mat-form-field>

        <button mat-raised-button color="primary" class="search-button" type="submit" [disabled]="!searchForm.valid || isLoading">
          <span *ngIf="!isLoading">Search</span>
          <mat-spinner diameter="20" *ngIf="isLoading"></mat-spinner>
        </button>
      </div>
    </form>

    <!-- Error Display -->
    <div *ngIf="error && !isLoading" class="error-container">
      <mat-icon>error_outline</mat-icon>
      <div class="error-content">
        <h4>{{ getErrorTitle() }}</h4>
        <p>{{ error }}</p>
        <button mat-button color="primary" (click)="onRetry()" *ngIf="canRetry()">Try Again</button>
      </div>
    </div>

    <!-- Search Results -->
    <div *ngIf="searchResult && !isLoading" class="results-container">

      <!-- Compact Unit Details Card -->
      <div class="unit-card-compact">
        <div class="unit-card-header">
          <div class="unit-title-section">
            <div class="unit-badge">
              <mat-icon>apartment</mat-icon>
            </div>
            <div class="unit-info">
              <h3>{{ searchResult.unit_details.property_unit_no }}</h3>
              <span class="property-name">{{ searchResult.unit_details.property_name }} • {{ searchResult.unit_details.type || 'N/A' }}</span>
            </div>
          </div>
          <span class="status-chip" [ngClass]="searchResult.unit_details.availability_status?.toLowerCase()">
            {{ searchResult.unit_details.availability_status }}
          </span>
        </div>

        <div class="unit-details-row">
          <div class="detail-item">
            <span class="label">Rent</span>
            <span class="value primary">{{ searchResult.unit_details.rent | currency:'AED ':'symbol':'1.0-0' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Deposit</span>
            <span class="value">{{ searchResult.unit_details.deposit | currency:'AED ':'symbol':'1.0-0' }}</span>
          </div>
          <div class="detail-item" *ngIf="searchResult.unit_details.vacant_date">
            <span class="label">Vacant From</span>
            <span class="value">{{ searchResult.unit_details.vacant_date | date:'dd MMM yyyy' }}</span>
          </div>
          <div class="detail-item" *ngIf="searchResult.unit_details.available_date_for_booking">
            <span class="label">Available</span>
            <span class="value success">{{ searchResult.unit_details.available_date_for_booking | date:'dd MMM yyyy' }}</span>
          </div>
        </div>

        <div class="unit-stats-row">
          <div class="stat-chip">
            <span class="count">{{ searchResult.total_contracts || 0 }}</span>
            <span class="label">Contracts</span>
          </div>
          <div class="stat-chip">
            <span class="count">{{ searchResult.total_bookings || 0 }}</span>
            <span class="label">Bookings</span>
          </div>
          <div class="stat-chip active">
            <span class="count">{{ searchResult.contracts?.active?.length || 0 }}</span>
            <span class="label">Active</span>
          </div>
        </div>
      </div>

      <!-- Active Contracts Section -->
      <div class="section-card" *ngIf="searchResult.contracts?.active?.length > 0">
        <div class="section-header-compact">
          <h4><mat-icon>description</mat-icon> Active Contracts</h4>
          <span class="count-badge active">{{ searchResult.contracts.active.length }}</span>
        </div>

        <div class="active-contracts-list">
          <div class="active-contract-card" *ngFor="let contract of searchResult.contracts.active">
            <!-- Contract Header -->
            <div class="contract-row-header">
              <div class="contract-main">
                <div class="tenant-avatar">{{ contract.tenant_name?.charAt(0) }}</div>
                <div class="contract-info">
                  <span class="tenant-name">{{ contract.tenant_name }}</span>
                  <span class="contract-no">#{{ contract.contract_no }}</span>
                </div>
              </div>
              <div class="contract-status">
                <span class="status-badge active">{{ contract.status }}</span>
              </div>
            </div>

            <!-- Contract Details Row -->
            <div class="contract-details-row">
              <div class="detail">
                <span class="label">Period</span>
                <span class="value">{{ contract.start_date | date:'dd MMM yyyy' }} → {{ contract.end_date | date:'dd MMM yyyy' }}</span>
              </div>
              <div class="detail">
                <span class="label">Rent</span>
                <span class="value rent">{{ contract.rent | currency:'AED ':'symbol':'1.0-0' }}</span>
              </div>
              <div class="detail">
                <span class="label">Deposit</span>
                <span class="value">{{ contract.deposit | currency:'AED ':'symbol':'1.0-0' }}</span>
              </div>
            </div>

            <!-- Cheque Summary for Active -->
            <div class="cheque-summary-compact" *ngIf="contract.cheque_summary">
              <div class="summary-row">
                <div class="summary-item">
                  <mat-icon class="success">check_circle</mat-icon>
                  <span class="count">{{ contract.cheque_summary.total_received }}</span>
                  <span class="label">Received</span>
                </div>
                <div class="summary-item">
                  <mat-icon class="warning">schedule</mat-icon>
                  <span class="count">{{ contract.cheque_summary.total_pending }}</span>
                  <span class="label">Pending</span>
                </div>
                <div class="summary-divider"></div>
                <div class="summary-breakdown">
                  <span class="breakdown-item">Rent: {{ contract.cheque_summary.rent?.received || 0 }}/{{ contract.cheque_summary.rent?.expected || 0 }}</span>
                  <span class="breakdown-item">Deposit: {{ contract.cheque_summary.deposit?.received || 0 }}/{{ contract.cheque_summary.deposit?.expected || 0 }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- History/Past Contracts Section -->
      <div class="section-card" *ngIf="searchResult.contracts?.history?.length > 0">
        <div class="section-header-compact">
          <h4><mat-icon>history</mat-icon> Past Contracts</h4>
          <span class="count-badge history">{{ searchResult.contracts.history.length }}</span>
        </div>

        <div class="history-table">
          <div class="history-row" *ngFor="let contract of searchResult.contracts.history">
            <div class="history-left">
              <span class="tenant">{{ contract.tenant_name }}</span>
              <span class="contract-no">#{{ contract.contract_no }}</span>
            </div>
            <div class="history-center">
              <span class="period">{{ contract.start_date | date:'dd MMM yy' }} → {{ contract.end_date | date:'dd MMM yy' }}</span>
            </div>
            <div class="history-right">
              <span class="amount">{{ contract.rent | currency:'AED ':'symbol':'1.0-0' }}</span>
              <span class="status-tag" [ngClass]="contract.status?.toLowerCase()">{{ contract.status }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Draft Contracts Section -->
      <div class="section-card" *ngIf="searchResult.contracts?.draft?.length > 0">
        <div class="section-header-compact">
          <h4><mat-icon>edit_note</mat-icon> Draft Contracts</h4>
          <span class="count-badge draft">{{ searchResult.contracts.draft.length }}</span>
        </div>

        <div class="history-table">
          <div class="history-row" *ngFor="let contract of searchResult.contracts.draft">
            <div class="history-left">
              <span class="tenant">{{ contract.tenant_name }}</span>
              <span class="contract-no">#{{ contract.contract_no }}</span>
            </div>
            <div class="history-center">
              <span class="period">{{ contract.start_date | date:'dd MMM yy' }} → {{ contract.end_date | date:'dd MMM yy' }}</span>
            </div>
            <div class="history-right">
              <span class="amount">{{ contract.rent | currency:'AED ':'symbol':'1.0-0' }}</span>
              <span class="status-tag draft">Draft</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cancelled Contracts Section -->
      <div class="section-card" *ngIf="searchResult.contracts?.cancelled?.length > 0">
        <div class="section-header-compact">
          <h4><mat-icon>cancel</mat-icon> Cancelled Contracts</h4>
          <span class="count-badge cancelled">{{ searchResult.contracts.cancelled.length }}</span>
        </div>

        <div class="history-table">
          <div class="history-row" *ngFor="let contract of searchResult.contracts.cancelled">
            <div class="history-left">
              <span class="tenant">{{ contract.tenant_name }}</span>
              <span class="contract-no">#{{ contract.contract_no }}</span>
            </div>
            <div class="history-center">
              <span class="period">{{ contract.start_date | date:'dd MMM yy' }} → {{ contract.end_date | date:'dd MMM yy' }}</span>
            </div>
            <div class="history-right">
              <span class="amount">{{ contract.rent | currency:'AED ':'symbol':'1.0-0' }}</span>
              <span class="status-tag cancelled">Cancelled</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Compact Bookings Section -->
      <div class="section-card" *ngIf="searchResult.bookings?.length > 0">
        <div class="section-header-compact">
          <h4><mat-icon>event_note</mat-icon> Bookings</h4>
          <span class="count-badge">{{ searchResult.bookings.length }}</span>
        </div>

        <div class="bookings-table">
          <div class="booking-row" *ngFor="let booking of searchResult.bookings">
            <div class="booking-main">
              <div class="booking-left">
                <span class="tenant">{{ booking.tenant_name }}</span>
                <span class="booking-meta">
                  <span class="booking-id">#{{ booking.id }}</span>
                  <span class="status-tag" [ngClass]="booking.status?.toLowerCase()">{{ booking.status }}</span>
                </span>
              </div>
              <div class="booking-period">
                {{ booking.start_date | date:'dd MMM yy' }} → {{ booking.end_date | date:'dd MMM yy' }}
              </div>
            </div>
            <div class="booking-financials">
              <div class="fin-item">
                <span class="label">Rent</span>
                <span class="value rent">{{ booking.rent_amount | currency:'AED ':'symbol':'1.0-0' }}</span>
              </div>
              <div class="fin-item">
                <span class="label">Deposit</span>
                <span class="value">{{ booking.deposit_amount | currency:'AED ':'symbol':'1.0-0' }}</span>
              </div>
              <div class="fin-item">
                <span class="label">Advance</span>
                <span class="value advance">{{ booking.advance_amount | currency:'AED ':'symbol':'1.0-0' }}</span>
              </div>
              <div class="fin-item payment-mode" *ngIf="booking.payment_mode">
                <span class="label">Payment</span>
                <span class="value mode">{{ booking.payment_mode }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </mat-dialog-content>
</div>
